<script setup>
import {onMounted, ref, defineProps, defineEmits, inject, watch, defineExpose} from "vue";
import { initializeMapBoundary } from "@/components/MapPage/MainMap/initializeMapBoundary";
import { initializeMap } from "@/components/MapPage/MainMap/initializeMap";
import axios from "axios";
import {initializeNews} from "@/components/MapPage/MainMap/initializeNews";
import {useMapStore} from "@/store/Map/map-store";
import {useLK} from "@/controllerLK/LKstore";
import {initializeMapRating} from "@/components/MapPage/MainMap/initializeMapRating";
import {initializeMapHandlers, removeListener} from "@/components/MapPage/MainMap/initializeMapHandlers";
import ZoomInIcon from "@/components/Icons/ZoomInIcon.vue";
import ZoomOutIcon from "@/components/Icons/ZoomOutIcon.vue";
import MapLegendsBlock from "@/components/MapPage/MapNews/MapLegendsBlock.vue";
import { t } from "@/controllerLK/AvalLocalization";

const prop = defineProps({
  map: {
    type: [Object, null],
    required: true,
  },
  filterKey: {
    type: Number,
    required: true,
    default: () => 0
  },
  isNewsListOpened: {
    type: Boolean,
    default: false,
    required: true
  },
});

const activeFilterTypeOption   = inject("activeFilterTypeOption");

const emit = defineEmits(["openNewsList", "updateFilter"]);

const LKStore = useLK();
const mapStore = useMapStore();

const mapRef = ref(null);
const getNewsError = ref(false);

const selectedDateFilter = inject("selectedDateFilter");
const selectedColorList = inject("selectedColorList");
const selectedTheme = inject("selectedTheme");
const selectedRubric = inject("selectedRubric");
const selectedSentimentFilter = inject("selectedSentimentFilter");

const filterKey = inject("filterKey");

const hideVectorFlower = () => {
  let vectorFlowerPoint;
  mapRef.value.getLayers().forEach((layer) => {
    if (layer.getSource().get("name") === "flower") vectorFlowerPoint = layer.getSource();
  });
  vectorFlowerPoint.clear();
  removeListener();
};

defineExpose({hideVectorFlower});

watch(filterKey, async () => {
  const filters = getFilters();

  hideVectorFlower();

  try {
    // Загрузка списка новостей и рейтинга для регионов
    await mapStore.getMapNewsList(LKStore.project.id, prop.map.name, selectedDateFilter.value, filters, activeFilterTypeOption.value);

    // Раскрашивание регионов по рейтингу
    initializeMapRating(mapRef.value, mapStore.mapRatingList);

    // Добавление точек с новостями
    initializeNews(mapRef.value, mapStore.mapNewsList, activeFilterTypeOption.value);
  } catch (error) {
    if (error.response && error.response.status >= 500 && error.response.status <= 599) {
      getNewsError.value = true;
    }
    console.error("Error loading map news:", error);
  }
});

onMounted(async () => {
  try {
    // Инициализация open layers
    mapRef.value = initializeMap();

    const filters = getFilters();

    // Инициализация хенлдеров на клик/ховер
    initializeMapHandlers(mapRef.value, (newsList) => { emit("openNewsList", newsList); });

    // Загрузка регионов и построение их на карте
    initializeMapBoundary(mapRef.value, await getBoundaries(), prop.map);

    const avalMapActiveFilterType = localStorage.getItem("avalMapActiveFilterType") || activeFilterTypeOption.value;

    try {
      // Загрузка списка новостей и рейтинга для регионов
      await mapStore.getMapNewsList(LKStore.project.id, prop.map.name, selectedDateFilter.value, filters, avalMapActiveFilterType);

      // Раскрашивание регионов по рейтингу
      initializeMapRating(mapRef.value, mapStore.mapRatingList);

      // Добавление точек с новостями
      initializeNews(mapRef.value, mapStore.mapNewsList);
    } catch (error) {
      if (error.response && error.response.status >= 500 && error.response.status <= 599) {
        getNewsError.value = true;
      }
      console.error("Error loading map news:", error);
    }

  } catch (e) {
    console.log(e);
  }

});

const getFilters = () => {
  const activeFilterType = localStorage.getItem("avalMapActiveFilterType") || activeFilterTypeOption.value;
  const selectedValues = activeFilterType === "sentiment"
      ? selectedSentimentFilter.value.map((item) => item.value)
      : selectedColorList.value.filter((value) => !isNaN(value));
  return {
    block_ids: selectedTheme.value.map((item) => Number(item.id)),
    rubric_ids: selectedRubric.value.map((item) => Number(item.id)),
    values: selectedValues,
  };
};

const getBoundaries = async () => {
  try {
    const mapFilePath = prop.map.map_file_path;
    const url = `${mapFilePath}`;
    return await axios.get(url).then((res) => res.data.features);
  } catch (e) {
    console.log(e);
  }
};

const zoomTo = (amount) => {
  const view = mapRef.value.getView();
  const zoom = view.getZoom();
  view.animate({zoom: zoom + amount});
};

</script>
<template>
  <div
    v-if="mapStore.isMapLoading"
    class="map-loader"
  >
    <div class="loader" />
  </div>
  <div
    id="map"
    class="map"
  />
  <div class="custom-zoom" :class="{'custom-zoom--sentiment': activeFilterTypeOption === 'sentiment'}">
    <div
      class="custom-zoom__item"
      @click="zoomTo(+1)"
    >
      <ZoomInIcon />
    </div>
    <div
      class="custom-zoom__item"
      @click="zoomTo(-1)"
    >
      <ZoomOutIcon />
    </div>
  </div>
  <MapLegendsBlock
    class="map-legends"
    :active-filter-type-option="activeFilterTypeOption"
  />
  <Modal 
    v-if="getNewsError" 
    :title="t('map_page.error.no_news')" 
    :confirm-text="t('map_page.button.reload')"
    :cancel-text="t('map_page.button.cancel')"
    teleport="#app"
    @close="getNewsError = false" 
    @cancel="getNewsError = false" 
    @confirm="getNewsError = false; emit('updateFilter')" 
  >
    {{ t('map_page.error.reload') }}
  </Modal>
</template>
<style lang="scss" scoped>
.map-legends {
  position: absolute;
  bottom: 14px;
  left: 14px;
  z-index: 1;
}

.custom-zoom {
  display: flex;
  gap: 5px;
  left: 14px;
  justify-content: center;
  position: absolute;
  bottom: 120px;
  z-index: 1;

  &--sentiment {
    bottom: 140px;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    user-select: none;
    border-radius: 8px;
    color: var(--icon-icon-primary-black);
    background: var(--background-01);
    box-shadow: 1px 2px 10px 0 var(--gray-gray50);
    width: 36px;
    height: 36px;
  }
}

.map {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

#popup {
  border-radius: 6px;
  padding: 10px 8px;
  color: black;
  max-width: 300px;
  min-width: 100px;
  position: relative;
  z-index: 3;
}

.map-loader {
  font-weight: 500;
  position: fixed;
  z-index: 1;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #06a2ff;
  gap: 10px;
  backdrop-filter: blur(13px);
}

.loader {
  position: relative;
  display: block;
  width: 44px;
  height: 44px;
  border-radius: 50%;

}
.loader::before {
  position: absolute;
  box-sizing: border-box;
  content: "";

  animation: prixClipFix 2s linear infinite ;
  border: 3px solid #7B87A5;
  border-radius: 50%;
  inset: 0;
}


@keyframes prixClipFix {
  0%   {clip-path:polygon(50% 50%,0 0,0 0,0 0,0 0,0 0)}
  25%  {clip-path:polygon(50% 50%,0 0,100% 0,100% 0,100% 0,100% 0)}
  50%  {clip-path:polygon(50% 50%,0 0,100% 0,100% 100%,100% 100%,100% 100%)}
  75%  {clip-path:polygon(50% 50%,0 0,100% 0,100% 100%,0 100%,0 100%)}
  100% {clip-path:polygon(50% 50%,0 0,100% 0,100% 100%,0 100%,0 0)}
}
</style>
